/**
 * ELSMERE FIRE PROTECTION DISTRICT - CONFIGURATION
 * =================================================
 * Generated from Fliplet JS files
 */

// ============================================
// BRANDING
// ============================================
export const BRANDING = {
  companyName: 'Elsmere Fire District',
  companyFullName: 'Elsmere Fire Protection District',
  appName: 'NarcoticTrack',
  appTagline: 'the most efficient and safe way to inventory controlled substances in EMS.',
  copyright: 'NarcoticTrack Inventory System, LLC',
  copyrightYear: '2025',
  primaryColor: '#c62828',
  secondaryColor: '#e0e0e0',
  backgroundColor: '#e8e8e8',
  cardBackground: '#f5f5f5',
  textColor: '#333333',
  textMuted: '#666666',
  borderColor: '#cccccc',
  pwaShortName: 'Elsmere NT',
  pwaFullName: 'Elsmere Fire District NarcoticTrack',
  themeColor: '#E30613',
  logoFilename: 'ElsmereLogo.png',
  ntLogoFilename: 'NTlogo.jpeg',
};

// ============================================
// SUPABASE CONFIGURATION
// ============================================
export const SUPABASE = {
  url: 'https://atwzglxbfjwmwiuwupms.supabase.co',
  anonKey: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImF0d3pnbHhiZmp3bXdpdXd1cG1zIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc0Nzc5MTIsImV4cCI6MjA4MzA1MzkxMn0.2pA-ZyJE0OUqdq2kKfoV0shRiC7aNBKHvZWTB1Hp08c',
};

// ============================================
// LOCATIONS
// ============================================
export const LOCATIONS = [
  { id: 'medic-416', name: 'MEDIC 416', type: 'squad', tableName: 'medic_416' },
  { id: 'medic-415-als', name: 'MEDIC 415 & ALS CABINET', type: 'squad', tableName: 'medic_415_als' },
];

export const getLocationsByType = (type) => LOCATIONS.filter(loc => loc.type === type);
export const getLocationById = (id) => LOCATIONS.find(loc => loc.id === id);

// ============================================
// MEDICATIONS (Default for most locations)
// ============================================
export const MEDICATIONS = [
  { id: 'fentanyl', displayName: 'Fentanyl', schedule: 'II' },
  { id: 'ketamine', displayName: 'Ketamine', schedule: 'III' },
  { id: 'morphine', displayName: 'Morphine', schedule: 'II' },
  { id: 'midazolam', displayName: 'Midazolam', schedule: 'IV' },
];

export const MEDICATION_IDS = MEDICATIONS.map(m => m.id);

// ============================================
// LOCATION-SPECIFIC MEDICATIONS
// ============================================
// For locations that track multiple sets of medications (like ALS + Medic 415)
export const LOCATION_MEDICATIONS = {
  'medic-415-als': {
    sections: [
      {
        title: 'ALS Cabinet',
        medications: [
          { id: 'als_ketamine', displayName: 'Ketamine' },
          { id: 'als_morphine', displayName: 'Morphine' },
          { id: 'als_fentanyl', displayName: 'Fentanyl' },
          { id: 'als_midazolam', displayName: 'Midazolam' },
        ]
      },
      {
        title: 'Medic 415',
        medications: [
          { id: 'medic415_ketamine', displayName: 'Ketamine' },
          { id: 'medic415_morphine', displayName: 'Morphine' },
          { id: 'medic415_fentanyl', displayName: 'Fentanyl' },
          { id: 'medic415_midazolam', displayName: 'Midazolam' },
        ]
      }
    ]
  }
};

// Get medications for a location (with sections if applicable)
export const getLocationMedications = (locationId) => {
  if (LOCATION_MEDICATIONS[locationId]) {
    return LOCATION_MEDICATIONS[locationId];
  }
  // Default: single section with standard medications
  return {
    sections: [{ title: null, medications: MEDICATIONS }]
  };
};

export const formatMedicationName = (id) => {
  // Check default medications
  const med = MEDICATIONS.find(m => m.id === id);
  if (med) return med.displayName;

  // Check location-specific medications
  for (const locConfig of Object.values(LOCATION_MEDICATIONS)) {
    for (const section of locConfig.sections) {
      const found = section.medications.find(m => m.id === id);
      if (found) return found.displayName;
    }
  }
  return id;
};

// ============================================
// SEAL TRACKING
// ============================================
export const SEAL_CONFIG = {
  squad: {
    enabled: true,
    fields: [
      { id: 'current_tag', displayName: 'Current Tag', required: true },
      { id: 'new_tag', displayName: 'New Tag', required: false, hint: '(If Applicable)' },
    ],
  },
  engine: { enabled: false, fields: [] },
  station: { enabled: false, fields: [] },
  office: { enabled: false, fields: [] },
};

export const getSealFields = (locationType) => {
  return SEAL_CONFIG[locationType]?.fields || [];
};

// ============================================
// FORM REQUIREMENTS
// ============================================
export const FORM_CONFIG = {
  requirePrimarySignature: true,
  requireSecondarySignature: true,
  requireSecondaryPinValidation: true,
  requireInventoryPhoto: false,
  requireRunNumber: false,
  requireReasonForChange: true,
  requireAdditionalNotes: false,
  showRunNumber: true,
  showAdditionalNotes: true,
  showInventoryPhoto: true,
  pinMinLength: 4,
  pinMaxLength: 6,
  sessionTimeout: 5 * 60 * 1000,
  reasonOptions: [
    'Daily Inventory',
    'Patient Use/Restock',
    'Expired Removal',
    'Damaged/Wasted',
    'Transfer',
    'Other'
  ],
};

// ============================================
// FEATURE FLAGS
// ============================================
export const FEATURES = {
  dailyDashboard: true,
  adminDashboard: true,
  chainOfCustodyReport: true,
  providerUsageReport: true,
  masterInventory: false,
  expirationReport: false,
  mileageReport: false,
  patientCareGuidelines: false,
  weightConverter: false,
  ivCalculator: false,
  glasgowComaScale: false,
  apgarCalculator: false,
  oxygenDuration: false,
  phoneDirectory: false,
  expirationTracking: false,
  expirationAlertDays: 30,
  deaForm41: true,
  aiComplianceInsights: true,
};

// ============================================
// ADMIN FUNCTIONS
// ============================================
export const ADMIN_FEATURES = [
  { id: 'users', name: 'Manage Users', path: '/users', enabled: true },
  { id: 'chain-of-custody', name: 'Chain Of Custody Report', path: '/chain-of-custody-report', enabled: true },
  { id: 'provider-usage', name: 'Provider Usage Report', path: '/provider-usage-report', enabled: true },
];

// ============================================
// CLIENT IDENTIFIER
// ============================================
export const CLIENT_ID = 'elsmere';

// ============================================
// EXPORT ALL
// ============================================
const CONFIG = {
  BRANDING,
  SUPABASE,
  LOCATIONS,
  MEDICATIONS,
  MEDICATION_IDS,
  SEAL_CONFIG,
  FORM_CONFIG,
  FEATURES,
  ADMIN_FEATURES,
  CLIENT_ID,
  getLocationsByType,
  getLocationById,
  formatMedicationName,
  getSealFields,
};

export default CONFIG;
